const CONTACT_FIELDS = [
  ['firstName', 'first_name'],
  ['lastName', 'last_name'],
  ['email', 'email'],
  ['phone', 'phone']
]

export function normalizeLeadGenAnswers (answers = {}) {
  const normalized = { ...answers }

  const existingContact = normalized.contact_info || {}
  const contactInfo = {}
  CONTACT_FIELDS.forEach(([formKey, flatKey]) => {
    contactInfo[formKey] = existingContact[formKey] || normalized[flatKey] || ''
  })
  if (CONTACT_FIELDS.some(([formKey]) => contactInfo[formKey])) {
    normalized.contact_info = contactInfo
  }

  if (!normalized.current_address) {
    const hasAddress = normalized.street_address || normalized.city ||
      normalized.state || normalized.current_zip_code
    if (hasAddress) {
      normalized.current_address = {
        street_address: normalized.street_address || '',
        city: normalized.city || '',
        state: normalized.state || '',
        current_zip_code: normalized.current_zip_code || '',
        years_at_address: normalized.years_at_address,
        months_at_address: normalized.months_at_address
      }
    }
  }

  if (!normalized.credit_check) {
    const hasCreditFields = normalized.credit_check_authorized != null ||
      normalized.date_of_birth || normalized.ssn
    if (hasCreditFields) {
      normalized.credit_check = {
        credit_check_authorized: normalized.credit_check_authorized === true ||
          normalized.credit_check_authorized === 'true',
        date_of_birth: normalized.date_of_birth,
        ssn: normalized.ssn,
        ...(normalized.econsent_accepted != null
          ? { econsent_accepted: normalized.econsent_accepted }
          : {})
      }
    }
  }

  return normalized
}

const RESPONSE_CONTACT_FLAT_KEYS = CONTACT_FIELDS.map(([, flatKey]) => flatKey)

export function answersToResponses (data = {}, { skipKeys = ['loan_officer_check', 'loan_type'] } = {}) {
  const responses = []
  for (const key in data) {
    if (skipKeys.includes(key) || data[key] === null || data[key] === undefined) continue
    if (key === 'loan_type' && data[key] === 'prequal') continue
    if (data.contact_info && RESPONSE_CONTACT_FLAT_KEYS.includes(key)) continue

    const value = data[key]
    if (Array.isArray(value)) {
      value.forEach(item => {
        responses.push({ responseKey: key, responseValue: item.toString() })
      })
    } else if (typeof value === 'object' && value !== null) {
      if (key === 'contact_info') {
        if (value.firstName) responses.push({ responseKey: 'first_name', responseValue: value.firstName.toString() })
        if (value.lastName) responses.push({ responseKey: 'last_name', responseValue: value.lastName.toString() })
        if (value.email) responses.push({ responseKey: 'email', responseValue: value.email.toString() })
        if (value.phone) responses.push({ responseKey: 'phone', responseValue: value.phone.toString() })
      } else {
        for (const objectKey in value) {
          if (skipKeys.includes(objectKey)) continue
          if (isEmpty(value[objectKey])) continue
          responses.push({ responseKey: objectKey, responseValue: value[objectKey].toString() })
        }
      }
    } else {
      if (isEmpty(value)) continue
      responses.push({ responseKey: key, responseValue: value.toString() })
    }
  }
  return responses
}

function isEmpty (value) {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'boolean') return false
  if (typeof value === 'number') return Number.isNaN(value)
  if (typeof value === 'object') {
    return Object.keys(value).length === 0 ||
      Object.values(value).every(v => isEmpty(v))
  }
  return false
}

function parseConfig (question) {
  if (!question?.config) return {}
  try {
    return typeof question.config === 'string' ? JSON.parse(question.config) : question.config
  } catch {
    return {}
  }
}

function getMultipleChoiceParentValue (answers, questionKey) {
  const stored = answers[questionKey]
  if (stored == null) return null
  if (typeof stored === 'object' && stored !== null && !Array.isArray(stored)) {
    return stored[questionKey] ?? stored.value ?? null
  }
  return stored
}

function followUpApplies (config, parentValue) {
  const followUp = config.follow_up_question
  if (!followUp || parentValue == null) return false
  const showFor = followUp.show_for_values || []
  return showFor.length === 0 || showFor.includes(parentValue)
}

function isContactInfoAnswered (answers) {
  const contact = answers.contact_info || {}
  return ['firstName', 'lastName', 'email', 'phone'].every(k => !isEmpty(contact[k]))
}

function isCurrentAddressAnswered (answers, questionKey) {
  const addr = answers[questionKey] || {}
  return !isEmpty(addr.street_address) && !isEmpty(addr.city) && !isEmpty(addr.state) &&
    !isEmpty(addr.current_zip_code) &&
    addr.years_at_address !== null && addr.years_at_address !== undefined &&
    addr.months_at_address !== null && addr.months_at_address !== undefined
}

function isCreditCheckAnswered (answers, questionKey) {
  const cc = answers[questionKey] || {}
  return !isEmpty(cc.credit_check_authorized) && !isEmpty(cc.date_of_birth) && !isEmpty(cc.ssn)
}

function isQuestionAnswered (question, answers) {
  const key = question.key
  const config = parseConfig(question)
  const componentType = question.component_type

  if (key === 'contact_info' || componentType === 'CONTACT_FORM') {
    return isContactInfoAnswered(answers)
  }

  if (componentType === 'CREDIT_CHECK_FORM') {
    return isCreditCheckAnswered(answers, key)
  }

  if (componentType === 'CURRENT_ADDRESS') {
    return isCurrentAddressAnswered(answers, key)
  }

  if (componentType === 'MULTIPLE_CHOICE' || componentType === 'YES_NO') {
    const parentValue = getMultipleChoiceParentValue(answers, key)
    if (isEmpty(parentValue)) return false

    if (!followUpApplies(config, parentValue)) return true

    const followUp = config.follow_up_question
    if (followUp.component_type === 'employment_duration') {
      const years = answers[followUp.years_key]
      const months = answers[followUp.months_key]
      return years !== null && years !== undefined &&
        months !== null && months !== undefined
    }

    return !isEmpty(answers[followUp.key])
  }

  return !isEmpty(answers[key])
}

export function findFirstUnansweredQuestionIndex (questions, answers) {
  if (!questions?.length) return 0

  for (let i = 0; i < questions.length; i++) {
    if (!isQuestionAnswered(questions[i], answers)) {
      return i
    }
  }

  return questions.length - 1
}
